// src/screens/AnalyticsPerformanceScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import downIcon from '../assets/dropdown-icon.png';

import AnalyticsPostPreviewCard from '../components/AnalyticsPostPreviewCard';
import DateRangeDrawer, { labelForRange } from '../components/popups/DateRangeDrawer';

import './AnalyticsPerformanceScreen.css';

function safeArr(a) {
  return Array.isArray(a) ? a : [];
}

export default function AnalyticsPerformanceScreen({ user }) {
  const navigate = useNavigate();

  const [range, setRange] = useState('7d'); // 'today' | '7d' | '30d' | 'all'
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null); // { planId, message }
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const title = 'Performance';
  const rangeLabel = useMemo(() => labelForRange(range), [range]);

  const goBack = () => {
    if (window.history.length > 1) return navigate(-1);
    return navigate('/analytics');
  };

  const load = async (nextRange) => {
    setLoading(true);
    setError('');
    setBlocked(null);

    try {
      const res = await api.get(`/analytics/me/performance?range=${nextRange}`);
      setData(res?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data;

      if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
        setBlocked({
          planId: payload?.planId || 'FREE',
          message:
            payload?.message ||
            'Analytics is available on Premium and Premium+ plans.',
        });
        setData(null);
      } else if (status === 401) {
        setError('Please login again.');
      } else {
        setError(payload?.message || 'Failed to load performance.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await load(range);
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const perf = data?.performance || {};

  // ✅ 5 sections (each shows TOP 5 horizontally)
  const sections = [
    { key: 'topByLikes', title: 'Top by Likes' },
    { key: 'topByComments', title: 'Top by Comments' },
    { key: 'topByShares', title: 'Top by Shares' },
    { key: 'topBySaves', title: 'Top by Saves' },
    { key: 'topByTips', title: 'Top by Tips' },
  ];

  const openPost = (postId) => {
    if (!postId) return;
    navigate(`/post/${postId}`);
  };

  return (
    <Screen>
      <div className="apf-container">
        <div className="apf-topbar">
          <img src={backIcon} alt="Back" className="apf-back" onClick={goBack} />
          <h2>{title}</h2>
          <div className="apf-spacer" />
        </div>

        {/* ✅ New: range dropdown pill (same as Overview) */}
        <div className="apf-rangeRow">
          <button
            type="button"
            className="apf-rangeBtn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Change date range"
            disabled={loading}
          >
            <span className="apf-rangeText">{rangeLabel}</span>
            <img
              src={downIcon}
              alt=""
              className="apf-rangeIcon"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </button>
        </div>

        {loading && (
          <div className="apf-loader">
            <div className="apf-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && blocked && (
          <div className="apf-card apf-blocked">
            <h3>Premium feature</h3>
            <p>{blocked.message}</p>
            <div className="apf-blocked-row">
              <span className="apf-pill">Your plan: {blocked.planId}</span>
              <button className="apf-upgrade" onClick={() => navigate('/plans')}>
                Upgrade
              </button>
            </div>
          </div>
        )}

        {!loading && !blocked && error && (
          <div className="apf-card apf-error">
            <h3>Couldn’t load</h3>
            <p>{error}</p>
            <button className="apf-upgrade" onClick={() => load(range)}>
              Retry
            </button>
          </div>
        )}

        {!loading && !blocked && !error && (
          <div className="apf-scroll">
            {sections.map((s) => {
              const list = safeArr(perf?.[s.key]).slice(0, 5);

              return (
                <div key={s.key} className="apf-section">
                  <div className="apf-section-head">
                    <h3>{s.title}</h3>
                    <span className="apf-count">{list.length}</span>
                  </div>

                  {list.length === 0 ? (
                    <div className="apf-empty">No posts found for this range.</div>
                  ) : (
                    <div className="apf-hscroll">
                      {list.map((p) => (
                        <AnalyticsPostPreviewCard
                          key={p.postId}
                          post={p}
                          onClick={() => openPost(p.postId)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ✅ Drawer */}
        <DateRangeDrawer
          open={drawerOpen}
          value={range}
          onClose={() => setDrawerOpen(false)}
          onChange={(v) => setRange(v)}
          title="Date ranges"
        />
      </div>
    </Screen>
  );
}
